// ===========================================
// SERVICE: Auditoría (CORE)
// ===========================================

import type { SupabaseClient } from '@supabase/supabase-js'
import type { Database } from '@/types/database'
import type {
  AuditLogConUsuario,
  AuditLogInsert,
  FiltrosAuditoria,
  AccionAuditoria,
  EntidadAuditoria,
  EstadisticasAuditoria,
} from '../types/auditoria'

/**
 * Servicio para gestión de auditoría.
 *
 * IMPORTANTE: Todos los métodos reciben el cliente Supabase autenticado como primer parámetro.
 * Usar con useSupabase() desde el hook que llame a estos métodos.
 *
 * @example
 * const { supabase } = useSupabase()
 * await auditoriaService.log(supabase!, userEmail, 'crear', 'cliente', clienteId)
 */
export const auditoriaService = {
  // ===========================================
  // Registro de Auditoría
  // ===========================================

  /**
   * Registra una acción en el log de auditoría.
   *
   * No pide la fila de vuelta: anon y authenticated no leen la columna detalle de audit_log, y un
   * insert que la devuelve falla entero.
   */
  async registrar(client: SupabaseClient<Database>, log: AuditLogInsert): Promise<void> {
    const userAgent = typeof window !== 'undefined' ? window.navigator.userAgent : null

    const insertData = {
      user_email: log.user_email,
      accion: log.accion,
      entidad: log.entidad,
      entidad_id: log.entidad_id || null,
      detalle: log.detalle || null,
      ip_address: log.ip_address || null,
      user_agent: userAgent,
      workspace: log.workspace || null,
      pantalla: log.pantalla || null,
      organizacion_id: log.organizacion_id || null,
    }

    const { error } = await client.from('audit_log').insert(insertData as never)

    if (error) {
      console.error('Error al registrar auditoría:', error)
    }
  },

  /**
   * Helper para registrar fácilmente
   */
  async log(
    client: SupabaseClient<Database>,
    userEmail: string,
    accion: AccionAuditoria,
    entidad: EntidadAuditoria,
    entidadId?: string,
    detalle?: Record<string, unknown>,
    contexto?: {
      workspace?: string
      pantalla?: string
      organizacion_id?: string
    }
  ): Promise<void> {
    try {
      await this.registrar(client, {
        user_email: userEmail,
        accion,
        entidad,
        entidad_id: entidadId,
        detalle,
        workspace: contexto?.workspace,
        pantalla: contexto?.pantalla,
        organizacion_id: contexto?.organizacion_id,
      })
    } catch (error) {
      console.error('Error en audit log:', error)
    }
  },

  // ===========================================
  // Consulta de Auditoría
  // ===========================================

  /**
   * Lista logs de auditoría con filtros
   */
  async listar(client: SupabaseClient<Database>, filtros?: FiltrosAuditoria, limite = 100): Promise<AuditLogConUsuario[]> {
    let query = client
      .from('vista_audit_log')
      .select('*')
      .order('created_at', { ascending: false })
      .limit(limite)

    if (filtros?.user_email) {
      query = query.eq('user_email', filtros.user_email)
    }

    if (filtros?.accion) {
      query = query.eq('accion', filtros.accion)
    }

    if (filtros?.entidad) {
      query = query.eq('entidad', filtros.entidad)
    }

    if (filtros?.entidad_id) {
      query = query.eq('entidad_id', filtros.entidad_id)
    }

    if (filtros?.workspace) {
      query = query.eq('workspace', filtros.workspace)
    }

    if (filtros?.organizacion_id) {
      query = query.eq('organizacion_id', filtros.organizacion_id)
    }

    if (filtros?.fecha_desde) {
      query = query.gte('created_at', filtros.fecha_desde)
    }

    if (filtros?.fecha_hasta) {
      const fechaHasta = new Date(filtros.fecha_hasta)
      fechaHasta.setDate(fechaHasta.getDate() + 1)
      query = query.lt('created_at', fechaHasta.toISOString())
    }

    if (filtros?.busqueda) {
      query = query.or(
        `user_email.ilike.%${filtros.busqueda}%,user_name.ilike.%${filtros.busqueda}%`
      )
    }

    const { data, error } = await query

    if (error) throw new Error(`Error al listar auditoría: ${error.message}`)
    return data as AuditLogConUsuario[]
  },

  /**
   * Obtiene logs de una entidad específica
   */
  async obtenerPorEntidad(
    client: SupabaseClient<Database>,
    entidad: EntidadAuditoria,
    entidadId: string
  ): Promise<AuditLogConUsuario[]> {
    return this.listar(client, { entidad, entidad_id: entidadId })
  },

  /**
   * Obtiene logs de un usuario específico
   */
  async obtenerPorUsuario(client: SupabaseClient<Database>, email: string, limite = 50): Promise<AuditLogConUsuario[]> {
    return this.listar(client, { user_email: email }, limite)
  },

  /**
   * Obtiene logs de un workspace específico
   */
  async obtenerPorWorkspace(client: SupabaseClient<Database>, workspace: string, limite = 100): Promise<AuditLogConUsuario[]> {
    return this.listar(client, { workspace }, limite)
  },

  // ===========================================
  // Exportación
  // ===========================================

  /**
   * Exporta logs a CSV
   */
  exportarCSV(logs: AuditLogConUsuario[]): string {
    const headers = [
      'Fecha',
      'Usuario',
      'Nombre',
      'Rol',
      'Acción',
      'Entidad',
      'ID Entidad',
      'Workspace',
      'Pantalla',
      'Detalle',
    ]

    const rows = logs.map((log) => [
      new Date(log.created_at).toLocaleString('es-AR'),
      log.user_email,
      log.user_name || '-',
      log.user_rol || '-',
      log.accion,
      log.entidad,
      log.entidad_id || '-',
      log.workspace || '-',
      log.pantalla || '-',
      log.detalle ? JSON.stringify(log.detalle) : '-',
    ])

    const BOM = '\uFEFF'
    const csvContent = [
      headers.join(';'),
      ...rows.map((row) => row.map((cell) => `"${cell}"`).join(';')),
    ].join('\n')

    return BOM + csvContent
  },

  /**
   * Descarga logs como archivo CSV
   */
  descargarCSV(logs: AuditLogConUsuario[], filename?: string): void {
    const csv = this.exportarCSV(logs)
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = filename || `auditoria_${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
  },

  // ===========================================
  // Estadísticas
  // ===========================================

  /**
   * Obtiene estadísticas de auditoría
   */
  async obtenerEstadisticas(client: SupabaseClient<Database>, dias = 30, workspace?: string): Promise<EstadisticasAuditoria> {
    const fechaDesde = new Date()
    fechaDesde.setDate(fechaDesde.getDate() - dias)

    let query = client
      .from('audit_log')
      .select('accion, entidad, user_email, workspace')
      .gte('created_at', fechaDesde.toISOString())

    if (workspace) {
      query = query.eq('workspace', workspace)
    }

    const { data, error } = await query

    if (error) throw new Error(`Error al obtener estadísticas: ${error.message}`)

    const logs = (data || []) as { accion: string; entidad: string; user_email: string; workspace: string | null }[]

    const porAccion: Record<string, number> = {}
    const porEntidad: Record<string, number> = {}
    const porWorkspace: Record<string, number> = {}
    const usuarios = new Set<string>()

    logs.forEach((log) => {
      porAccion[log.accion] = (porAccion[log.accion] || 0) + 1
      porEntidad[log.entidad] = (porEntidad[log.entidad] || 0) + 1
      if (log.workspace) {
        porWorkspace[log.workspace] = (porWorkspace[log.workspace] || 0) + 1
      }
      usuarios.add(log.user_email)
    })

    return {
      totalRegistros: logs.length,
      porAccion,
      porEntidad,
      porWorkspace,
      usuariosActivos: usuarios.size,
    }
  },
}

// ===========================================
// Helpers de Auditoría
// ===========================================

/**
 * Crea un wrapper para auditar operaciones automáticamente
 */
export function crearAuditor(
  client: SupabaseClient<Database>,
  userEmail: string,
  contexto?: {
    workspace?: string
    pantalla?: string
    organizacion_id?: string
  }
) {
  const ctx = contexto || {}

  return {
    crear: (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'crear', entidad, entidadId, detalle, ctx),

    editar: (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'editar', entidad, entidadId, detalle, ctx),

    eliminar: (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'eliminar', entidad, entidadId, detalle, ctx),

    cambiarEstado: (entidad: EntidadAuditoria, entidadId: string, estadoAnterior: string, estadoNuevo: string) =>
      auditoriaService.log(client, userEmail, 'cambiar_estado', entidad, entidadId, {
        estado_anterior: estadoAnterior,
        estado_nuevo: estadoNuevo,
      }, ctx),

    subir: (entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'subir', 'documento', entidadId, detalle, ctx),

    exportar: (entidad: EntidadAuditoria, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'exportar', entidad, undefined, detalle, ctx),

    vincular: (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'vincular', entidad, entidadId, detalle, ctx),

    desvincular: (entidad: EntidadAuditoria, entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'desvincular', entidad, entidadId, detalle, ctx),

    emitir: (entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'emitir', 'comprobante', entidadId, detalle, ctx),

    anular: (entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'anular', 'comprobante', entidadId, detalle, ctx),

    importar: (entidad: EntidadAuditoria, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'importar', entidad, undefined, detalle, ctx),

    facturar: (entidadId: string, detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'facturar', 'comprobante', entidadId, detalle, ctx),

    generarLote: (detalle?: Record<string, unknown>) =>
      auditoriaService.log(client, userEmail, 'generar_lote', 'movimiento', undefined, detalle, ctx),
  }
}
